/* eslint-disable consistent-return */
import { Mongo } from 'meteor/mongo';
import SimpleSchema from 'simpl-schema';
import { map } from 'lodash';

import { LAYER_TYPES, LAYER_SUBTYPES } from '../Layers/Layers';

/**
 * A layer server object for iTasker.
 *
 * @typedef {object} MapServerElement
 * @property {string} _id - The layer server id.
 * @property {string} owner - The owner id.
 * @property {string} createdAt - The date when it was created.
 * @property {string} updatedAt - The date when it was last updated.
 * @property {string} deleted - Indicates whether it is in the trash or not.
 * @property {string} name - The name for the layer server.
 * @property {string} [sourceId] - The id for the source that generates this layer server.
 * @property {string} type - The view type for the layer server.
 * One of [XYZTiles,WMS,TMS,GeoJSON,CSV].
 * @property {string} url - The url that points to this layer server in the geoserver.
 */

export const MAPSERVERELEMENTS_TYPES = {
  XYZTiles: 'XYZTiles',
  TMS: 'TMS',
  WMS: 'WMS',
  WMTS: 'WMTS',
  GEOIMAGE: 'GeoImage',
  GEOJSON: 'GeoJSON',
  MAPBOX: 'mapBox',
  MAPBOXGL: 'mapBoxGL',
  EXCEL: 'excel',
  GOOGLE_SHEETS: 'googlesheets',
  CSV: 'CSV',
  OGR: 'ogr',
  properties: {
    XYZTiles: {
      name: 'XYZTiles',
      value: 'XYZTiles',
      humanValue: 'xyzTiles'
    },
    TMS: {
      name: 'TMS',
      value: 'TMS',
      humanValue: 'TMS'
    },
    WMS: {
      name: 'WMS',
      value: 'WMS',
      humanValue: 'WMS'
    },
    WMTS: {
      name: 'WMTS',
      value: 'WMTS',
      humanValue: 'WMTS'
    },
    GEOIMAGE: {
      name: 'GEOIMAGE',
      value: 'GeoImage',
      humanValue: 'GeoImage'
    },
    GEOJSON: {
      name: 'GEOJSON',
      value: 'GeoJSON',
      humanValue: 'GeoJSON'
    },
    MAPBOX: {
      name: 'MAPBOX',
      value: 'mapBox',
      humanValue: 'MapBox'
    },
    MAPBOXGL: {
      name: 'MAPBOXGL',
      value: 'mapBoxGL',
      humanValue: 'MapBox GL'
    },
    CSV: {
      name: 'CSV',
      value: 'CSV',
      humanValue: 'CSV'
    },
    EXCEL: {
      name: 'EXCEL',
      value: 'excel',
      humanValue: 'Excel'
    },
    GOOGLE_SHEETS: {
      name: 'GOOGLE_SHEETS',
      value: 'googlesheets',
      humanValue: 'Google Sheets'
    },
    OGR: {
      name: 'OGR',
      value: 'ogr',
      humanValue: 'OGR'
    }
  }
};

/**
 * Get the layer type and subtype from the layer server type.
 *
 * @param {string} mapServerElementType - The layer server type.
 * @returns {object} The layer type and subtype.
 */
export const getLayerTypesForLayerServerType = mapServerElementType => {
  switch (mapServerElementType) {
    case MAPSERVERELEMENTS_TYPES.TMS:
    case MAPSERVERELEMENTS_TYPES.WMS:
    case MAPSERVERELEMENTS_TYPES.WMTS:
    case MAPSERVERELEMENTS_TYPES.GEOJSON:
    case MAPSERVERELEMENTS_TYPES.MAPBOX:
    case MAPSERVERELEMENTS_TYPES.MAPBOXGL:
    case MAPSERVERELEMENTS_TYPES.OGR:
      return { layerType: LAYER_TYPES.SPATIAL, layerSubType: mapServerElementType };
    case MAPSERVERELEMENTS_TYPES.EXCEL:
    case MAPSERVERELEMENTS_TYPES.GOOGLE_SHEETS:
      return { layerType: LAYER_TYPES.THEMATIC, layerSubType: mapServerElementType };
    case MAPSERVERELEMENTS_TYPES.XYZTiles:
      return { layerType: LAYER_TYPES.SPATIAL, layerSubType: LAYER_SUBTYPES.TILE_SERVER };
    case MAPSERVERELEMENTS_TYPES.CSV:
      return { layerType: LAYER_TYPES.THEMATIC, layerSubType: LAYER_SUBTYPES.CSV };
    default:
      return { layerType: '', layerSubType: '' };
  }
};

const MapServerElements = new Mongo.Collection('MapServerElements');

MapServerElements.allow({
  insert: () => false,
  update: () => false,
  remove: () => false
});

MapServerElements.deny({
  insert: () => true,
  update: () => true,
  remove: () => true
});

MapServerElements.schema = new SimpleSchema({
  owner: {
    type: String,
    label: 'The ID of the user this layer server belongs to'
  },
  createdAt: {
    type: String,
    label: 'The date this layer server was created in the database',
    autoValue() {
      if (this.isInsert) return new Date().toISOString();
    }
  },
  updatedAt: {
    type: String,
    label: 'The date this layer server was last updated',
    autoValue() {
      if (this.isInsert || this.isUpdate) return new Date().toISOString();
    }
  },
  name: {
    type: String,
    label: 'A name to identify the layer server'
  },
  sourceId: {
    type: String,
    label: 'The Id of the source that generated the layer server',
    optional: true
  },
  type: {
    type: String,
    label: 'The type of the layer server added',
    allowedValues: map(MAPSERVERELEMENTS_TYPES.properties, 'value')
  },
  url: {
    type: SimpleSchema.RegEx.Url,
    label: 'The url that points to the layer server in question in an external server'
  },
  deleted: {
    type: String,
    label: 'The date the layer server was deleted',
    autoValue() {
      if (this.isInsert) return 'no';
    }
  }
});

MapServerElements.attachSchema(MapServerElements.schema);

export default MapServerElements;
